import React from "react";
import {
  Box,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Theme,
} from "@mui/material";
import AppTableHeaderCell from "../../components/common/pages/AppTableHeaderCell";
import AppTablePagenation from "../../components/common/pages/AppTablePagenation";
import { SortType } from "../../../models/SortType";
import {
  PURCHASE_TRANSACTION_STATUS_TABS,
  PurchaseTransactionStatusTabId,
  UserPurchaseTransactionApiRow,
} from "../../../models/Swagstore/UserPurchaseTransactions";
import { formatWalletDate } from "../swagstore-wallet/walletFormatters";
import { ShipmentStatus } from "../../../models/Shipments/ShipmentStatus";
import { listingStatusTabsStyles } from "../../../styles/listingStatusTabsSx";
import {
  getModernPageColors,
  modernEmptyStateSx,
  modernTableBodyCellSx,
  modernTableHeaderCellSx,
  modernTablePaperSx,
  modernTableRowSx,
} from "../../../styles/modernListingPageSx";

interface SwagstorePurchaseTransactionsTableProps {
  rows: UserPurchaseTransactionApiRow[];
  loading: boolean;
  pageNo: number;
  limit: number;
  totalCount: number;
  sortType: SortType;
  activeStatusTab: PurchaseTransactionStatusTabId;
  onStatusTabChange: (
    event: React.SyntheticEvent,
    newValue: PurchaseTransactionStatusTabId,
  ) => void;
  onSortChange: (sortType: SortType) => void;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onRowClick?: (row: UserPurchaseTransactionApiRow) => void;
}

const CREATED_DATE_COLUMN = "created_date";

const formatAmount = (
  amount: number | null | undefined,
  currency?: string | null,
) => {
  if (amount == null) return "—";
  const symbol = currency === "INR" || !currency ? "₹" : "";
  return `${symbol}${amount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

const SwagstorePurchaseTransactionsTable: React.FC<
  SwagstorePurchaseTransactionsTableProps
> = ({
  rows,
  loading,
  pageNo,
  limit,
  totalCount,
  sortType,
  activeStatusTab,
  onStatusTabChange,
  onSortChange,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
}) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);

  return (
    <Paper
      sx={{
        ...modernTablePaperSx(theme),
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Tabs
        value={activeStatusTab}
        onChange={onStatusTabChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={(t) => ({
          ...listingStatusTabsStyles(t),
          px: { xs: 1, sm: 1.5 },
          bgcolor: t.palette.customColors.lightBlue,
          borderBottom: `1px solid ${t.palette.customColors.lightGray}`,
        })}
      >
        {PURCHASE_TRANSACTION_STATUS_TABS.map((tab) => (
          <Tab
            key={tab.id}
            label={tab.label.toUpperCase()}
            id={`purchase-transaction-tab-${tab.id}`}
            value={tab.id}
          />
        ))}
      </Tabs>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 240,
          }}
        >
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ ...modernEmptyStateSx(theme), minHeight: 240 }}>
          <Typography variant="body1">
            No purchase transactions found.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <TableContainer sx={{ maxHeight: "calc(100vh - 340px)" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <AppTableHeaderCell
                    minWidth="120px"
                    sortColumn={CREATED_DATE_COLUMN}
                    sortBy={CREATED_DATE_COLUMN}
                    sortDirection={sortType}
                    onSortChange={(_column, newSortType) =>
                      onSortChange(newSortType)
                    }
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Date
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="110px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Shipment ID
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="140px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Placed For
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="120px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Shipment Status
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="120px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Wallet Amount
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="120px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    User Paid
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="90px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Currency
                  </AppTableHeaderCell>

                  <AppTableHeaderCell
                    minWidth="180px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Placed By
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="120px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Transaction ID
                  </AppTableHeaderCell>

                  <AppTableHeaderCell
                    minWidth="120px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Last Modified Date
                  </AppTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    key={`purchase-txn-${row.shipment_id}-${row.transaction_id ?? index}`}
                    hover
                    onClick={() => onRowClick?.(row)}
                    sx={{
                      ...modernTableRowSx(theme),
                      cursor: onRowClick ? "pointer" : undefined,
                    }}
                  >
                    <TableCell sx={modernTableBodyCellSx(theme)}>
                      {formatWalletDate(row.created_date)}
                    </TableCell>
                    <TableCell sx={modernTableBodyCellSx(theme)}>{row.shipment_id}</TableCell>
                    <TableCell sx={modernTableBodyCellSx(theme)}>
                      {row.placed_for_user || "—"}
                      <br />
                      {row.placed_for_user_email || "—"}
                    </TableCell>
                    <TableCell sx={modernTableBodyCellSx(theme)}>
                      {(row.shipment_status &&
                        ShipmentStatus.find(
                          (status) => status.id === row.shipment_status,
                        )?.value) ||
                        "—"}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...modernTableBodyCellSx(theme),
                        fontWeight: 700,
                        color: colors.accent,
                      }}
                    >
                      {formatAmount(row.wallet_amount, row.currency)}
                    </TableCell>
                    <TableCell sx={modernTableBodyCellSx(theme)}>
                      {formatAmount(row.user_paid_amount, row.currency)}
                    </TableCell>
                    <TableCell sx={modernTableBodyCellSx(theme)}>{row.currency || "—"}</TableCell>

                    <TableCell sx={modernTableBodyCellSx(theme)}>
                      {row.placed_by_user || "—"}
                      <br />
                      {row.placed_by_user_email || "—"}
                    </TableCell>
                    <TableCell sx={modernTableBodyCellSx(theme)}>
                      {row.transaction_id ?? "—"}
                    </TableCell>

                    <TableCell sx={modernTableBodyCellSx(theme)}>
                      {formatWalletDate(row.last_modified_date)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <AppTablePagenation
            fontSize="12px"
            pageNo={pageNo}
            limit={limit}
            totalCount={totalCount}
            onPageChange={onPageChange}
            handleRowsChange={onRowsPerPageChange}
          />
        </Box>
      )}
    </Paper>
  );
};

export default React.memo(SwagstorePurchaseTransactionsTable);
